/**
 * DelayNode - Web Audio delay with feedback and wet/dry mix
 *
 * Endpoints:
 * - audioIn (input): audio to delay
 * - audioOut (output): delayed + dry audio
 * - delayTime: delay time in seconds
 * - feedback: feedback amount (0-0.95)
 * - mix: wet/dry balance (0 = dry, 1 = wet)
 *
 * Internal chain (wet/dry with feedback):
 *   audioIn ──┬──> dryGain ──────────────────┬──> mergeGain ──> audioOut
 *             └──> delay ──> wetGain ─────────┘
 *                    ^                |
 *                    └── feedbackGain ┘
 *
 * Metadata passthrough is automatic via AudioBaseNode.
 */

import { AudioBaseNode, type AudioDependencies } from '../AudioBaseNode';
import { Domain } from '@nodes/Endpoint';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import { audio, params } from '@nodes/TypedEndpoints';

const endpoints = {
  ...audio({
    audioIn: { isInput: true },
    audioOut: { isOutput: true }
  }),
  ...params({
    delayTime: { label: 'Time', value: 0.3, min: 0, max: 1, step: 0.01 },
    feedback: { value: 0.3, min: 0, max: 0.95, step: 0.01 },
    mix: { label: 'Wet/Dry', value: 0.5, min: 0, max: 1, step: 0.01 },
  })
};

export class DelayNode extends AudioBaseNode<typeof endpoints> {
  private delayWebNode: any = null;
  private dryGain: GainNode | null = null;
  private wetGain: GainNode | null = null;
  private feedbackGain: GainNode | null = null;
  private mergeGain: GainNode | null = null;

  constructor(id: string, deps: AudioDependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();

    const ctx = this.audioContext;

    this.delayWebNode = ctx.createDelay(1.0);
    this.dryGain = ctx.createGain();
    this.wetGain = ctx.createGain();
    this.feedbackGain = ctx.createGain();
    this.mergeGain = ctx.createGain();

    // Set initial values
    const mix = this.params.mix.value;
    this.delayWebNode.delayTime.value = this.params.delayTime.value;
    this.feedbackGain.gain.value = this.params.feedback.value;
    this.dryGain.gain.value = 1 - mix;
    this.wetGain.gain.value = mix;

    // Wire internal chain
    // Dry path: audioIn → dryGain → mergeGain
    this.audio.audioIn.connect(this.dryGain);
    this.dryGain.connect(this.mergeGain);

    // Wet path: audioIn → delay → wetGain → mergeGain
    const inputGainNode = this.getGainNode('audioIn');
    inputGainNode.connect(this.delayWebNode);
    this.delayWebNode.connect(this.wetGain);
    this.wetGain.connect(this.mergeGain);

    // Feedback: delay → feedbackGain → delay
    this.delayWebNode.connect(this.feedbackGain);
    this.feedbackGain.connect(this.delayWebNode);

    // Output: mergeGain → audioOut
    this.audio.audioOut.connect(this.mergeGain);
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    const value = payload.value;
    switch (endpointId) {
      case 'delayTime':
        if (this.delayWebNode) this.delayWebNode.delayTime.value = value;
        break;
      case 'feedback':
        if (this.feedbackGain) this.feedbackGain.gain.value = value;
        break;
      case 'mix':
        if (this.dryGain && this.wetGain) {
          this.dryGain.gain.value = 1 - value;
          this.wetGain.gain.value = value;
        }
        break;
    }
  }

  dispose(): void {
    this.delayWebNode?.disconnect();
    this.dryGain?.disconnect();
    this.wetGain?.disconnect();
    this.feedbackGain?.disconnect();
    this.mergeGain?.disconnect();

    this.delayWebNode = null;
    this.dryGain = null;
    this.wetGain = null;
    this.feedbackGain = null;
    this.mergeGain = null;

    super.dispose();
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const delayNodeDef: NodeDef<DelayNode> = {
  type: 'Delay',
  shortName: 'delay',
  info: 'Delay effect with feedback and wet/dry mix.',
  domain: Domain.Audio,
  category: 'effect',
  endpoints,
  ctor: DelayNode
};
